/**
 * `orders.v_activity.row_id` is a COMPOSITE key, not an order id.
 *
 * The view emits `'order:' || o.id` so a row is unique across the sources it
 * unions (orders and invoices). Treating it as a bare uuid is how the order
 * history's "open this order" link and its Reorder button both broke: the URL
 * became `/orders/order%3A<uuid>`, PostgREST was asked for
 * `id=eq.order:<uuid>`, and an invalid uuid comes back 400 — which the order
 * page rendered as the flat, wrong "We couldn't find that order."
 *
 * ⚠ It hid for a while because only SOME rows use it. An order that has been
 * invoiced links to `/invoices/<qbo id>` instead, and 153 of 200 live orders
 * are invoiced — so the broken link was only ever on the orders that have NOT
 * been invoiced yet, which is exactly the set a customer would want to open
 * or change.
 *
 * Keep `rowId` as the opaque key (React lists, dedupe) and use this to get the
 * order id.
 */
export function activityOrderId(rowId: string | null | undefined): string | null {
  const s = (rowId ?? '').trim();
  if (!s) return null;
  const i = s.indexOf(':');
  // No prefix at all: the view could be changed to emit bare ids, and a bare
  // id is still a usable order id — don't break on the friendlier shape.
  const id = i === -1 ? s : s.slice(0, i) === 'order' ? s.slice(i + 1) : '';
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ? id : null;
}

/**
 * What to print as a row's identifier.
 *
 * ⚠ Nothing in this codebase ever writes `orders.order_number` — 204 of 204
 * orders have it NULL — so a heading of `{row.orderNumber}` alone renders as an
 * empty bold line on every row. The number a customer and our dispatchers both
 * actually quote is the Service Fusion job number, which every order carries.
 *
 * The sub-line is only returned when it is not repeating the heading, so a row
 * never says "Order #123" twice.
 */
export function rowHeading(row: {
  orderNumber: string | null;
  sfJobId: string | null;
  invoiceDocNumber?: string | null;
  rowId: string;
}): { main: string; sub: string | null } {
  const num = (row.orderNumber ?? '').trim();
  const job = (row.sfJobId ?? '').trim();
  const doc = (row.invoiceDocNumber ?? '').trim();

  if (num) return { main: num, sub: job ? `Order #${job}` : null };
  if (job) return { main: `Order #${job}`, sub: null };
  if (doc) return { main: `#${doc}`, sub: null };

  // Last resort: never an empty heading. The tail of the row key is enough to
  // tell two rows apart and to quote back to us on the phone.
  const tail = row.rowId.replace(/^order:/, '').slice(-6).toUpperCase();
  return { main: tail ? `Order \u2026${tail}` : 'Order', sub: null };
}
